<template>
  <div :class="wrapperClasses">
    <label v-if="label" :for="textareaId" class="ui-textarea__label">
      {{ label }}
      <span v-if="required" class="ui-textarea__required">*</span>
    </label>
    <textarea
      :id="textareaId"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :rows="rows"
      :maxlength="maxLength"
      :class="textareaClasses"
      @input="handleInput"
      @blur="handleBlur"
      @focus="handleFocus"
    ></textarea>
    <div v-if="error || helperText || showCharCount" class="ui-textarea__footer">
      <div class="ui-textarea__helper">
        <span v-if="error" class="ui-textarea__error">{{ error }}</span>
        <span v-else-if="helperText" class="ui-textarea__helper-text">
          <template v-if="showCharCount && maxLength">
            {{ characterCount }} / {{ maxLength }} - {{ helperText }}
          </template>
          <template v-else>
            {{ helperText }}
          </template>
        </span>
      </div>
      <div v-if="showCharCount && maxLength && !helperText" class="ui-textarea__char-count">
        {{ characterCount }} / {{ maxLength }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type { Size } from "./types";

interface Props {
  modelValue?: string;
  label?: string;
  placeholder?: string;
  size?: Size;
  rows?: number;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCharCount?: boolean;
  resize?: "none" | "vertical" | "horizontal" | "both";
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  rows: 4,
  disabled: false,
  readonly: false,
  required: false,
  showCharCount: false,
  resize: "vertical",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const isFocused = ref(false);
const textareaId = computed(
  () => props.id || `textarea-${Math.random().toString(36).substr(2, 9)}`
);

const characterCount = computed(() => props.modelValue?.length || 0);

const wrapperClasses = computed(() => {
  const classes = ["ui-textarea"];
  classes.push(`ui-textarea--${props.size}`);
  if (props.disabled) classes.push("ui-textarea--disabled");
  if (props.error) classes.push("ui-textarea--error");
  return classes;
});

const textareaClasses = computed(() => {
  const classes = ["ui-textarea__field"];
  if (isFocused.value) classes.push("ui-textarea__field--focused");
  classes.push(`ui-textarea__field--resize-${props.resize}`);
  return classes;
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit("update:modelValue", target.value);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};
</script>

<style scoped>
.ui-textarea {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ui-textarea__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.ui-textarea__required {
  color: var(--color-danger);
  font-size: 1.1rem;
  margin-left: 2px;
}

.ui-textarea__field {
  width: 100%;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background-color: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  padding: 10px 12px;
  transition: all var(--transition-normal);
  line-height: 1.5;
}

.ui-textarea__field::placeholder {
  color: var(--color-text-placeholder);
  font-weight: 300;
  font-size: var(--font-size-sm);
}

.ui-textarea__field:hover:not(:disabled) {
  border-color: var(--color-border-hover);
}

.ui-textarea__field:focus {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-textarea__field:disabled {
  background-color: var(--color-bg-disabled);
  cursor: not-allowed;
  opacity: 0.5;
  border-color: var(--color-border-light);
}

.ui-textarea__field--resize-none {
  resize: none;
}

.ui-textarea__field--resize-vertical {
  resize: vertical;
}

.ui-textarea__field--resize-horizontal {
  resize: horizontal;
}

.ui-textarea__field--resize-both {
  resize: both;
}

.ui-textarea__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-1);
}

.ui-textarea__helper {
  font-size: var(--font-size-xs);
}

.ui-textarea__error {
  color: var(--color-danger);
}

.ui-textarea__helper-text {
  color: var(--color-text-placeholder);
}

.ui-textarea__char-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-placeholder);
}

/* Size: sm */
.ui-textarea--sm .ui-textarea__field {
  padding: 8px 10px;
  font-size: var(--font-size-xs);
}

/* Size: lg */
.ui-textarea--lg .ui-textarea__field {
  padding: 12px 16px;
  font-size: 0.9rem;
}

/* Error state */
.ui-textarea--error .ui-textarea__field {
  border-color: var(--color-danger);
}

.ui-textarea--error .ui-textarea__field:focus {
  border-color: var(--color-danger) !important;
  box-shadow: var(--ring-danger) !important;
}
</style>
